import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Campaign"` — the people a campaign left out, and why (AC-31).
 *
 * The prototype has no such panel; P14-02 and AC-31 require it, because a campaign reaching
 * fewer people than its list has must say WHY, and "on your do-not-call list" is a reason the
 * customer cannot switch off. The reason filter is what the manifest's `filtered-empty` state
 * belongs to: picking a reason nobody was skipped for says so in words, with the way back.
 *
 * The per-reason counts are the API's own `count_by_reason` over the WHOLE campaign; the rows
 * are one page of at most 100. So a reason whose count is above zero but which has no row on
 * this page says "on a later page" rather than "nobody", and the counts never come from
 * counting the page.
 *
 * AMENDED 2026-09-30 (plan-008 ST-UA): a person the dialler could not ring because the runtime
 * refused the dial (the trunk not verified, the model resting, WeTalk's SIP edge not answering, …)
 * is skipped with that refusal as the reason (design §22.11). Each refusal somebody was skipped for
 * joins the filter after AC-31's six, in words.
 */
import { useQuery } from "@tanstack/react-query";
import { Button, Select, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from "@wetalk/ui";
import { useId, useState, type ReactNode } from "react";

import { SKIP_REASON_SEQUENCE, campaignRefusalText, countText, skipFilterLabel, skipWordLabel } from "./campaign-copy.js";
import { useGateway } from "../../api/provider.js";
import { campaignSkipQuery } from "../../api/query.js";
import { CAMPAIGN_DIAL_REFUSAL, type CampaignSkipWordView } from "../../api/slice/campaign.js";

export interface CampaignSkipPanelProps {
  readonly campaignId: string;
}

type SkipFilter = CampaignSkipWordView | "all";

const SKIP_WORD_SEQUENCE: readonly CampaignSkipWordView[] = [...SKIP_REASON_SEQUENCE, ...CAMPAIGN_DIAL_REFUSAL];

function filterOf(raw: string): SkipFilter {
  const found = SKIP_WORD_SEQUENCE.find((one) => one === raw);
  return found === undefined ? "all" : found;
}

export function CampaignSkipPanel({ campaignId }: CampaignSkipPanelProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const filterId = useId();
  const [cursor, setCursor] = useState<string | null>(null);
  const [filter, setFilter] = useState<SkipFilter>("all");
  const page = useQuery(campaignSkipQuery(gateway, campaignId, cursor));

  const heading = (
    <div className="c-camp-section-head">
      <div>
        <h2 className="c-camp-section-title">{portalText("operations", "people_not_called_81b8df96")}</h2>
        <p className="c-camp-section-dek">
          {portalText("operations", "who_this_campaign_left_out_and_why_nobody_on_your_do_not_c_b07ad1e7")}</p>
      </div>
    </div>
  );

  if (page.isError) {
    return (
      <>
        {heading}
        <p className="c-note c-note-critical">
          {portalText("operations", "the_people_this_campaign_left_out_could_not_be_listed_so_n_7b26ec36")}{" "}
          {campaignRefusalText(page.error)}
        </p>
      </>
    );
  }
  if (page.data === undefined) {
    return (
      <>
        {heading}
        <p className="c-note">{portalText("operations", "fetching_the_people_this_campaign_left_out_b304dce6")}</p>
      </>
    );
  }

  const data = page.data;
  if (data.totalCount === 0) {
    return (
      <>
        {heading}
        <div className="c-panel c-panel-dashed">
          <p className="c-empty-title">{portalText("operations", "nobody_has_been_left_out_e4302676")}</p>
          <p className="c-empty-dek">
            {portalText("operations", "every_person_this_campaign_has_reached_so_far_was_inside_t_b8a24b0c")}</p>
        </div>
      </>
    );
  }

  const countOf = (reason: CampaignSkipWordView): number => data.countByReason[reason] ?? 0;
  // AC-31's reasons always; a refusal only once somebody was skipped for it.
  const offered = SKIP_WORD_SEQUENCE.filter(
    (one) => (SKIP_REASON_SEQUENCE as readonly string[]).includes(one) || countOf(one) > 0,
  );
  const shown = filter === "all" ? data.row : data.row.filter((one) => one.skipReason === filter);
  const filteredCount = filter === "all" ? data.totalCount : countOf(filter);

  return (
    <>
      {heading}
      <div className="c-camp-skip-filter">
        <label htmlFor={filterId}>{portalText("operations", "show_d97d1ee3")}</label>
        <Select
          id={filterId}
          value={filter}
          onChange={(event) => {
            setFilter(filterOf(event.target.value));
          }}
        >
          <option value="all">{skipFilterLabel("all", data.totalCount)}</option>
          {offered.map((one) => (
            <option key={one} value={one}>
              {skipFilterLabel(one, countOf(one))}
            </option>
          ))}
        </Select>
      </div>

      {shown.length === 0 ? (
        <div className="c-panel c-panel-dashed">
          <p className="c-empty-title">
            {filteredCount === 0 ? portalText("operations", "nobody_was_left_out_for_that_reason_832c36fc") : portalText("operations", "none_on_this_page_10bb1359")}
          </p>
          <p className="c-empty-dek">
            {filteredCount === 0
              ? portalText("operations", "no_one_in_this_campaign_was_skipped_as_value_f3a1deb1", { v0: String(skipWordLabel(filter).toLowerCase()) })
              : portalText("operations", "value_people_were_skipped_for_this_reason_they_are_on_anot_52dcc776", { v0: String(countText(filteredCount)) })}{" "}
            <button
              type="button"
              className="c-camp-link-button"
              onClick={() => {
                setFilter("all");
              }}
            >
              {portalText("operations", "show_every_reason_6ec54702")}</button>
          </p>
        </div>
      ) : (
        <div className="c-camp-scroll">
          <Table caption={portalText("operations", "people_this_campaign_did_not_call_with_the_reason_9b8dca4b")}>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{portalText("operations", "person_8c41ae88")}</TableHeaderCell>
                <TableHeaderCell>{portalText("operations", "why_they_were_not_called_ff5ab75a")}</TableHeaderCell>
                <TableHeaderCell>{portalText("operations", "when_769bb19e")}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((one) => (
                <TableRow key={one.campaignAttemptId}>
                  <TableCell>
                    <span className="c-camp-who">{one.displayName === null ? portalText("operations", "not_named_f5e6238c") : one.displayName}</span>
                    <span className="c-camp-number">{one.e164}</span>
                  </TableCell>
                  <TableCell>{skipWordLabel(one.skipReason)}</TableCell>
                  <TableCell>{one.whenText}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {cursor === null && data.nextCursor === null ? null : (
        <div className="c-row">
          {cursor === null ? null : (
            <Button
              variant="ghost"
              onClick={() => {
                setCursor(null);
              }}
            >
              {portalText("operations", "back_to_the_first_page_8722a00c")}</Button>
          )}
          {data.nextCursor === null ? null : (
            <Button
              variant="secondary"
              onClick={() => {
                setCursor(data.nextCursor);
              }}
            >
              {portalText("operations", "next_page_4bfc194b")}</Button>
          )}
        </div>
      )}
    </>
  );
}
